import type { PostPublic, PostMutationOptions } from '@repo/types/posts';

import { useEditPost } from '@repo/api/post/useEditPost';
import { BookText, InfoIcon } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogBody, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { LoadingSwap } from '@/components/ui/loading-swap';

/**
 * Props for the ActivatePost component.
 * @property {PostPublic} [post] - The post to activate/deactivate.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 * @property {PostMutationOptions} [options] - The options for the post.
 */
interface ActivatePostProps {
  post?: PostPublic;
  open: boolean;
  onOpenChange: (value: boolean) => void;
  options?: PostMutationOptions;
}

export default function ActivatePost(activePostProps: ActivatePostProps) {
  const { post, open, onOpenChange, options } = activePostProps;
  const { mutateAsync, isPending, isError, errorMessage } = useEditPost(options);

  const onSubmit = () => {
    if (post) {
      mutateAsync({ id: post.id, active: !post.active }).then(() => {
        onOpenChange(false);
      });
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon size={'lg'}>
              <BookText />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>Zmienić aktywność posta ?</AlertDialogTitle>
              <AlertDialogDescription className={'text-muted-foreground text-xs'}>Zmień akywność posta aby go ukryć / pokazać.</AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <div className={'flex flex-col p-4'}>
            <p className={'text-sx text-muted-foreground'}>
              Czy na pewno chcesz zmienić aktywność posta <b>{post?.name} ?</b>
            </p>
            <Alert
              variant={'destructive'}
              data-visible={isError}
              className="overflow-hidden opacity-100 transition-[height,opacity] duration-400 data-[visible=false]:max-h-0 data-[visible=false]:p-0 data-[visible=false]:opacity-0 data-[visible=true]:h-fit data-[visible=true]:opacity-100"
            >
              <InfoIcon />
              <AlertTitle>{errorMessage.title}</AlertTitle>
              <AlertDescription>{errorMessage.detail}</AlertDescription>
            </Alert>
          </div>
        </AlertDialogBody>
        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant={'tertiary'}>Anuluj</AlertDialogCancel>
          <Button disabled={isPending} onClick={onSubmit}>
            <LoadingSwap isLoading={isPending}>Potwierdzam</LoadingSwap>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
